/**
 * Sentry — crash reporting ONLY, the same posture as the mobile app (its ADR
 * `2026-07-24-frontend-client-logging` in train-with-me-app): crashes go to Sentry, tracing stays
 * on the backend's OpenTelemetry, and session replay is declined everywhere. Each option below
 * that turns something off is declining a Sentry product on purpose, not a default left unread.
 *
 * The DSN is a public write-only ingest address (the same class of value as the Supabase anon
 * key), baked into the bundle at build time. Absent means crash reporting is off and the app runs
 * unchanged — dev builds run without it by default. The DSN's origin must also be present in the
 * CSP's `connect-src`, which `vite.config.ts` derives from the same variable, so the two cannot
 * disagree within one build.
 */

import * as Sentry from '@sentry/react';

import { scrubBreadcrumb } from './breadcrumbScrub';

const dsn: string | undefined = import.meta.env.VITE_SENTRY_DSN;

/**
 * Which deployment this build reports as — the label Sentry events are filtered by. The non-dev
 * fallback is "staging", never "production": a build that does not say otherwise must not claim
 * to be one (the same rule the mobile app's env module states for its deploy label).
 */
const deployEnv: string =
  import.meta.env.VITE_DEPLOY_ENV ?? (import.meta.env.DEV ? 'development' : 'staging');

let initialized = false;

/**
 * Arms crash reporting. Call once, as early in the boot as possible — anything that crashes
 * before this runs is unreported by definition.
 */
export function initCrashReporting(): void {
  if (initialized) return;

  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn('[sentry] VITE_SENTRY_DSN not set — crash reporting disabled.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: deployEnv,

    // Errors only: no performance tracing (the backend owns tracing via OTLP) and no replay
    // integration is registered, so no session is ever recorded. Release health (crash-free
    // rate) stays on — it is a crash metric, the one product this module exists for.
    tracesSampleRate: 0,

    // Trainers' screens hold their clients' names, measurements and training history. The SDK
    // must never attach identifying data on its own initiative; what a crash carries is decided
    // here, not by a default.
    sendDefaultPii: false,

    // A navigation breadcrumb keeps the path only: a query can carry a one-time code.
    beforeBreadcrumb: scrubBreadcrumb,
  });

  initialized = true;
}
